import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Printer, Users, AlertTriangle } from "lucide-react";
import { api, apiError, API } from "@/lib/api";

const DEPT_LABELS = { cucina: "CUCINA", sala_bar: "SALA / BAR", pulizie: "PULIZIE" };
const DEPT_COLORS = { cucina: "#F59E0B", sala_bar: "#38BDF8", pulizie: "#A78BFA" };

export default function DepartmentSheet() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/sheets/${token}`);
      setData(r.data);
    } catch (e) {
      setError(apiError(e));
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (task) => {
    try {
      const r = await api.post(`/sheets/${token}/toggle`, { task_id: task.id, done: !task.done });
      setData((d) => ({ ...d, sheet: r.data }));
    } catch (e) { toast.error(apiError(e)); }
  };

  if (error)
    return (
      <div className="dept-dark flex flex-col items-center justify-center px-6 text-center">
        <AlertTriangle className="w-12 h-12 text-amber-400 mb-4" />
        <p className="text-xl font-bold">{error}</p>
      </div>
    );
  if (!data) return <div className="dept-dark flex items-center justify-center">Caricamento…</div>;

  const { sheet, quote } = data;
  const done = sheet.tasks.filter((t) => t.done).length;
  const pct = sheet.tasks.length ? Math.round((done / sheet.tasks.length) * 100) : 0;
  const color = DEPT_COLORS[sheet.department];

  return (
    <div className="dept-dark pb-16">
      <header className="px-5 pt-8 pb-5 border-b border-white/10">
        <div className="max-w-xl mx-auto">
          <div className="text-xs font-bold tracking-[0.3em] mb-1" style={{ color }}>BLISS CAFÉ · SCHEDA OPERATIVA</div>
          <h1 className="text-3xl font-extrabold">{DEPT_LABELS[sheet.department]}</h1>
          <p className="text-slate-300 mt-2 text-lg">
            {quote.event.type} · {quote.event.date} · {quote.event.time_start}–{quote.event.time_end}
          </p>
          <div className="flex items-center gap-2 mt-2 text-slate-300 text-lg">
            <Users className="w-5 h-5" /> {quote.guests.adults} adulti + {quote.guests.teens || 0} ragazzi + {quote.guests.children} bambini
          </div>
          {quote.guests.dietary && (
            <div className="mt-3 bg-amber-500/15 border border-amber-500/40 rounded-xl px-4 py-3 text-amber-300 font-bold text-lg">
              ⚠ {quote.guests.dietary}
            </div>
          )}
          <div className="mt-5">
            <div className="flex justify-between text-sm font-bold mb-1">
              <span data-testid="dept-progress-label">{pct}% completato</span>
              <span>{done}/{sheet.tasks.length}</span>
            </div>
            <div data-testid="dept-progress-bar" className="h-3 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full transition-all duration-300" style={{ width: `${pct}%`, background: "#22C55E" }} />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-5 mt-6 space-y-3">
        {sheet.tasks.map((task) => (
          <button key={task.id} data-testid={`checklist-checkbox-${task.id}`} onClick={() => toggle(task)}
            className={`w-full text-left rounded-2xl p-4 flex items-start gap-4 border-2 transition-colors ${task.done ? "bg-emerald-500/10 border-emerald-500/50" : "bg-[#131C2E] border-[#38BDF8]/30"}`}>
            <span className={`w-10 h-10 rounded-xl border-4 flex items-center justify-center shrink-0 ${task.done ? "bg-emerald-500 border-emerald-500" : "border-[#38BDF8]"}`}>
              {task.done && <Check className="w-7 h-7 text-white" strokeWidth={3} />}
            </span>
            <span className="min-w-0">
              {task.time && <span className="font-mono-num font-bold text-lg mr-2" style={{ color }}>{task.time}</span>}
              <span className={`text-lg font-bold leading-tight ${task.done ? "line-through text-slate-500" : ""}`}>{task.title}</span>
              {task.detail && <span className="block text-slate-400 text-sm mt-1">{task.detail}</span>}
            </span>
          </button>
        ))}
      </main>

      <div className="max-w-xl mx-auto px-5 mt-8">
        <a data-testid="sheet-pdf-link" href={`${API}/sheets/${token}/pdf`} target="_blank" rel="noreferrer"
          className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl border-2 border-white/20 text-lg font-bold hover:bg-white/5">
          <Printer className="w-5 h-5" /> Stampa PDF di backup
        </a>
      </div>
    </div>
  );
}
